import { useMemo } from 'react';
import { mapMethodToParams, PAID_IN_FULL_METHOD_KEY } from '../../../services/schedulePreview';
import { resolveAppliedDeposit, type AppliedDeposit } from '../../../services/appliedDeposit';
import { useSchedulePreview } from '../../../hooks/useSchedulePreview';
import type { SchedulePreviewParams, CollectionTarget, SchedulePreview } from '../../../types/schedule.types';
import { transmittedInstalments } from '../../../services/evenMonthlyTerm';
import type { EvenMonthlyDerivation } from '../../../services/evenMonthlyTerm';
import type { SchedulingMethod } from '@/features/products/types/products.types';

/** One payment of the whole amount, collected in full at checkout (the anchor day). */
const paidInFullSnapshot = (amountMinor: number, anchor: string): SchedulePreview => ({
    numPayments: 1,
    schedule: [
        { number: 1, date: anchor, payment: amountMinor, principal: amountMinor, interest: 0, balance: 0 },
    ],
    meta: {
        total_repayable: amountMinor,
        total_interest: 0,
        total_principal: amountMinor,
        total_fees: 0,
        balloon: 0,
    },
});

export function useSchedulePreviewParams(options: {
    isReadOnly: boolean;
    canPreviewSchedule: boolean;
    selectedMethod: SchedulingMethod | null;
    basketGrossMinor: number;
    isDepositBearing: boolean;
    depositMinor: number | undefined;
    isItemBasedFamily: boolean;
    collectionTargets: CollectionTarget[];
    derivedNumInstalments: number | undefined;
    depositExceedsEffectiveMax: boolean;
    leadOutOfBounds: boolean;
    /** A priced service falls due before it could be collected — no valid schedule exists. */
    pastDueForScheduling: boolean;
    isEvenMonthlyFamily: boolean;
    evenMonthly: EvenMonthlyDerivation;
    /** The even-monthly count the control transmits — the chosen value, or its clamped
     *  preselect. Overrides the derived count when set. */
    evenMonthlyInstalmentsOverride?: number;
    anchor: string;
    /** Minor units, forwarded to the reduced-DD preview when dealer-collected. */
    dealerCollectedDepositAmount?: number;
}): {
    params: SchedulePreviewParams | null;
    data: SchedulePreview | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
    isPlaceholderData: boolean;
    appliedDeposit: AppliedDeposit | undefined;
    scheduleSetDepositMinor: number | undefined;
} {
    const {
        isReadOnly,
        canPreviewSchedule,
        selectedMethod,
        basketGrossMinor,
        isDepositBearing,
        depositMinor,
        isItemBasedFamily,
        collectionTargets,
        derivedNumInstalments,
        depositExceedsEffectiveMax,
        leadOutOfBounds,
        pastDueForScheduling,
        isEvenMonthlyFamily,
        evenMonthly,
        evenMonthlyInstalmentsOverride,
        anchor,
        dealerCollectedDepositAmount,
    } = options;

    const params: SchedulePreviewParams | null = useMemo(() => {
        if (isReadOnly || !canPreviewSchedule || !selectedMethod || basketGrossMinor <= 0) {
            return null;
        }
        if (isItemBasedFamily && collectionTargets.length === 0) {
            return null;
        }
        if (depositExceedsEffectiveMax) {
            return null;
        }
        if (leadOutOfBounds) {
            return null;
        }
        // A service due before its first collection has no valid schedule — withhold the
        // preview entirely rather than show one that collects after the service.
        if (pastDueForScheduling) {
            return null;
        }
        if (isEvenMonthlyFamily && evenMonthly.kind === 'not-offerable') {
            return null;
        }
        const amountMinor = isItemBasedFamily
            ? collectionTargets[collectionTargets.length - 1].amount
            : basketGrossMinor;
        return mapMethodToParams(selectedMethod, {
            amountMinor,
            isDepositBearing,
            depositMinor: isDepositBearing ? depositMinor : undefined,
            collectionTargets: isItemBasedFamily ? collectionTargets : undefined,
            numInstalments: isItemBasedFamily
                ? derivedNumInstalments
                : isEvenMonthlyFamily
                  ? (evenMonthlyInstalmentsOverride ?? transmittedInstalments(evenMonthly))
                  : undefined,
            dealerCollectedDepositAmount: isDepositBearing
                ? dealerCollectedDepositAmount
                : undefined,
        });
    }, [
        isReadOnly,
        canPreviewSchedule,
        selectedMethod,
        basketGrossMinor,
        isDepositBearing,
        depositMinor,
        isItemBasedFamily,
        collectionTargets,
        derivedNumInstalments,
        depositExceedsEffectiveMax,
        leadOutOfBounds,
        pastDueForScheduling,
        isEvenMonthlyFamily,
        evenMonthly,
        evenMonthlyInstalmentsOverride,
        dealerCollectedDepositAmount,
    ]);

    // Paid-in-full is a single charge collected in full at checkout — no schedule. It skips the
    // preview request (which would anchor a lone instalment a period out); snapshot synthesised today.
    const isPaidInFull = selectedMethod?.key === PAID_IN_FULL_METHOD_KEY;
    const fetched = useSchedulePreview(isPaidInFull ? null : params);

    const data = isPaidInFull && params !== null ? paidInFullSnapshot(params.amount, anchor) : fetched.data;
    const isLoading = isPaidInFull ? false : fetched.isLoading;
    const isFetching = isPaidInFull ? false : fetched.isFetching;
    const error = isPaidInFull ? null : fetched.error;
    const isPlaceholderData = isPaidInFull ? false : fetched.isPlaceholderData;

    const appliedDeposit =
        params !== null && data !== undefined && !error && !isPlaceholderData
            ? resolveAppliedDeposit({ params, snapshot: data, scheduleAnchorDate: anchor })
            : undefined;

    const scheduleSetDepositMinor =
        appliedDeposit?.kind === 'amount' && appliedDeposit.source === 'schedule'
            ? appliedDeposit.amountMinor
            : undefined;

    return {
        params,
        data,
        isLoading,
        isFetching,
        error,
        isPlaceholderData,
        appliedDeposit,
        scheduleSetDepositMinor,
    };
}
